import { useState, useEffect, useRef } from "react";
import client, { apiErr } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Plus, Loader2, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { resolveAddress } from "@/components/AddressAutocomplete";

// Searchable single-select with optional "create new" from the typed query.
// onCreate(query) should perform creation AND selection (async).
// When addressSuggest is true, live UK address suggestions (geocoded) appear as
// you type; picking one calls onCreateFromAddress(suggestion) (async).
export function SearchSelect({
  value, onChange, options = [], placeholder = "Select…", searchPlaceholder = "Search…",
  emptyText = "No matches", onCreate, createLabel = "Create", testid = "search-select",
  disabled = false, className = "", addressSuggest = false, onCreateFromAddress,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [addr, setAddr] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const timer = useRef(null);

  const selected = options.find((o) => o.value === value);
  const qq = query.trim().toLowerCase();
  const filtered = qq ? options.filter((o) => (o.label || "").toLowerCase().includes(qq)) : options;
  const exact = options.some((o) => (o.label || "").trim().toLowerCase() === qq);
  const showCreate = !!onCreate && !!query.trim() && !exact;

  useEffect(() => {
    if (!addressSuggest) return;
    if (timer.current) clearTimeout(timer.current);
    const q = query.trim();
    if (q.length < 2) { setAddr([]); setAddrLoading(false); return; }
    setAddrLoading(true);
    timer.current = setTimeout(async () => {
      try { const { data } = await client.get("/geo/autocomplete", { params: { q } }); setAddr(data.results || []); }
      catch { setAddr([]); }
      finally { setAddrLoading(false); }
    }, 450);
    return () => timer.current && clearTimeout(timer.current);
  }, [query, addressSuggest]);

  const doCreate = async () => {
    setCreating(true);
    try { await onCreate(query.trim()); setOpen(false); setQuery(""); setAddr([]); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const pickAddress = async (r) => {
    if (r.isPostcode) { setQuery(r.address); return; }
    setCreating(true);
    try {
      const full = await resolveAddress(r);
      if (onCreateFromAddress) await onCreateFromAddress(full);
      else if (onCreate) await onCreate(full.address);
      setOpen(false); setQuery(""); setAddr([]);
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const showAddr = addressSuggest && query.trim().length >= 2;
  const nothing = filtered.length === 0 && !showCreate && !showAddr;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setQuery(""); setAddr([]); } }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span className={cn("truncate", !selected && "text-muted-foreground")}>{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[16rem]" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder={searchPlaceholder} data-testid={`${testid}-input`} />
          <CommandList>
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((o) => (
                  <CommandItem
                    key={o.value}
                    value={o.value}
                    data-testid={`${testid}-option-${o.value}`}
                    onSelect={() => { onChange(o.value); setOpen(false); setQuery(""); setAddr([]); }}
                  >
                    <Check className={cn("h-4 w-4", value === o.value ? "opacity-100 text-brand" : "opacity-0")} />
                    <span className="truncate">{o.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {showCreate && !showAddr && (
              <CommandGroup>
                <CommandItem value={`__create__${query}`} onSelect={doCreate} data-testid={`${testid}-create`} className="text-brand">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  <span className="truncate">{createLabel} “{query.trim()}”</span>
                </CommandItem>
              </CommandGroup>
            )}
            {showAddr && (
              <CommandGroup heading={addr.length ? "Address suggestions" : undefined}>
                {addrLoading && (
                  <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching addresses…</div>
                )}
                {addr.map((r, i) => (
                  <CommandItem key={`addr-${i}`} value={`__addr__${i}`} onSelect={() => pickAddress(r)} data-testid={`${testid}-addr-${i}`} className="items-start text-brand">
                    <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                    <span className="leading-snug text-slate-700">{r.address}{r.postcode && !(r.address || "").includes(r.postcode) && <span className="ml-1 font-semibold text-slate-900">· {r.postcode}</span>}</span>
                  </CommandItem>
                ))}
                {!addrLoading && addr.length === 0 && showCreate && (
                  <CommandItem value={`__create__${query}`} onSelect={doCreate} data-testid={`${testid}-create`} className="text-brand">
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    <span className="truncate">{createLabel} “{query.trim()}”</span>
                  </CommandItem>
                )}
              </CommandGroup>
            )}
            {nothing && <div className="py-6 text-center text-sm text-muted-foreground">{emptyText}</div>}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
